/**
 * Expense Service (BE-5a/b/c, BE-19) — real API.
 *
 * Expenses, categories, groups, recurring templates and issued cheques.
 * The backend is snake_case; this service returns camelCase and takes camelCase.
 * A TC user's typing centre comes from the token, so no typingCenterId is sent.
 */

import ApiService from '../api.service';
import { keysCamelToSnake, unwrapData, unwrapList, unwrapPage } from '../../utils/apiConvert';

// Drop empty strings so optional fields reach the backend as absent, not "".
const clean = (obj) => Object.fromEntries(
  Object.entries(obj).filter(([, v]) => v !== '' && v !== undefined),
);
const body = (data) => keysCamelToSnake(clean(data));

/**
 * "Pay From" choices, encoded as `${type}:${id}`:
 *   BANK / CHEQUE → an active OPERATIONS bank account, CASH → an active CASH account,
 *   CREDIT_CARD → an active TC credit card.
 */
export const encodePayFrom = (type, id) => (type && id ? `${type}:${id}` : '');
export const decodePayFrom = (value) => {
  if (!value) return { paymentAccountType: undefined, paymentAccountId: undefined };
  const [paymentAccountType, paymentAccountId] = value.split(':');
  return { paymentAccountType, paymentAccountId };
};

export const expenseService = {
  // ── Expenses ──────────────────────────────────────────────────────────────
  list: async (params = {}) =>
    unwrapPage(await ApiService.get('/api/expenses', { params: clean(params) })),

  pendingReview: async (params = {}) =>
    unwrapPage(await ApiService.get('/api/expenses/pending-review', { params: clean(params) })),

  // Money total of everything the filters match (refunds count negative); params as list, without paging.
  total: async (params = {}) =>
    Number(unwrapData(await ApiService.get('/api/expenses/total', { params: clean(params) }))?.total ?? 0),

  summary: async (params = {}) =>
    unwrapData(await ApiService.get('/api/expenses/summary', { params: clean(params) })),

  getById: async (id) => unwrapData(await ApiService.get(`/api/expenses/${id}`)),

  create: async (data) => unwrapData(await ApiService.post('/api/expenses', body(data))),

  update: async (id, data) => unwrapData(await ApiService.put(`/api/expenses/${id}`, body(data))),

  remove: async (id) => ApiService.delete(`/api/expenses/${id}`),

  approve: async (id) => unwrapData(await ApiService.post(`/api/expenses/${id}/approve`)),

  reject: async (id, reason) => unwrapData(await ApiService.post(`/api/expenses/${id}/reject`, { reason })),

  reverse: async (id, reason) => unwrapData(await ApiService.post(`/api/expenses/${id}/reverse`, { reason })),

  // data: { paymentAccountType, paymentAccountId, paidAt, chequeNumber?, chequeDate? }
  markPaid: async (id, data) => unwrapData(await ApiService.post(`/api/expenses/${id}/mark-paid`, body(data))),

  submit: async (id) => unwrapData(await ApiService.post(`/api/expenses/${id}/submit`)),

  skip: async (id) => unwrapData(await ApiService.post(`/api/expenses/${id}/skip`)),

  attachReceipt: async (id, file) => {
    const form = new FormData();
    form.append('file', file);
    return unwrapData(await ApiService.post(`/api/expenses/${id}/receipt`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }));
  },

  // Returns { downloadUrl, filename, contentType }; 404 when no receipt is attached.
  receiptUrl: async (id) => unwrapData(await ApiService.get(`/api/expenses/${id}/receipt-url`)),

  history: async (id) =>
    unwrapList(await ApiService.get(`/api/audit-log/EXPENSE/${id}`)).items,

  // ── Categories & groups ───────────────────────────────────────────────────
  getCategories: async () => unwrapList(await ApiService.get('/api/expense-categories')).items,

  createCategory: async (data) => unwrapData(await ApiService.post('/api/expense-categories', body(data))),

  updateCategory: async (id, data) =>
    unwrapData(await ApiService.put(`/api/expense-categories/${id}`, body(data))),

  // Returns { created, skipped } counts.
  seedDefaultCategories: async () =>
    unwrapData(await ApiService.post('/api/expense-categories/seed-defaults')),

  getGroups: async () => unwrapList(await ApiService.get('/api/expense-groups')).items,

  createGroup: async (data) => unwrapData(await ApiService.post('/api/expense-groups', body(data))),

  updateGroup: async (id, data) => unwrapData(await ApiService.put(`/api/expense-groups/${id}`, body(data))),

  // ── Recurring templates (BE-5c) ───────────────────────────────────────────
  getTemplates: async () => unwrapList(await ApiService.get('/api/expense-templates')).items,

  createTemplate: async (data) => unwrapData(await ApiService.post('/api/expense-templates', body(data))),

  updateTemplate: async (id, data) =>
    unwrapData(await ApiService.put(`/api/expense-templates/${id}`, body(data))),

  deleteTemplate: async (id) => ApiService.delete(`/api/expense-templates/${id}`),

  // ── Issued cheques (BE-19) ────────────────────────────────────────────────
  // params: { status, bankAccountId, from, to }
  getCheques: async (params = {}) =>
    unwrapList(await ApiService.get('/api/cheques', { params: clean(params) })).items,

  clearCheque: async (id, date) => unwrapData(await ApiService.post(`/api/cheques/${id}/clear`, { date })),

  bounceCheque: async (id, date, reason) =>
    unwrapData(await ApiService.post(`/api/cheques/${id}/bounce`, { date, reason })),

  cancelCheque: async (id, reason) => unwrapData(await ApiService.post(`/api/cheques/${id}/cancel`, { reason })),

  // ── Lookups for the forms ─────────────────────────────────────────────────
  /**
   * Pay From options. `includeCheque` adds "{Bank} (Cheque)" per operations bank
   * (not offered on refunds or recurring templates).
   */
  getPayFromOptions: async ({ includeCheque = true } = {}) => {
    const [banks, cards] = await Promise.all([
      ApiService.getTcBankAccounts().then(unwrapList).then((r) => r.items).catch(() => []),
      ApiService.get('/api/tc-credit-cards').then(unwrapList).then((r) => r.items).catch(() => []),
    ]);
    const active = banks.filter((b) => b.isActive !== false);
    const bankLabel = (b) => [b.bankName, b.accountName].filter(Boolean).join(' - ') || 'Bank account';
    const options = [
      ...active.filter((b) => b.accountType === 'OPERATIONS').map((b) => ({
        value: encodePayFrom('BANK', b.id), label: bankLabel(b), group: 'Bank', balance: b.balance,
      })),
      ...active.filter((b) => b.accountType === 'CASH').map((b) => ({
        value: encodePayFrom('CASH', b.id), label: b.accountName || b.bankName || 'Cash', group: 'Cash', balance: b.balance,
      })),
      ...cards.filter((c) => c.active !== false).map((c) => ({
        value: encodePayFrom('CREDIT_CARD', c.id),
        label: `${c.cardName || c.cardType} ••${c.lastFour}`,
        group: 'Credit card',
      })),
    ];
    if (includeCheque) {
      options.push(...active.filter((b) => b.accountType === 'OPERATIONS').map((b) => ({
        value: encodePayFrom('CHEQUE', b.id), label: `${bankLabel(b)} (Cheque)`, group: 'Cheque',
      })));
    }
    return options;
  },

  // Team members of the caller's typing centre (salary payee picker).
  getTeamMembers: async (typingCenterId) => {
    if (!typingCenterId) return [];
    const res = await ApiService.getTypingCenterUsers(typingCenterId);
    return unwrapList(res).items.filter((u) => u.active !== false);
  },
};

export default expenseService;
